import { useMemo } from 'react'
import { ringBounds, type Ring } from './geo'

/**
 * The no-basemap fallback: an inline SVG of the polygon itself.
 *
 * This is what renders when no Mapbox token is configured — in CI, in an offline
 * deployment, and during a token rotation. It answers "does this polygon look
 * like the right shape, roughly in the right place?" with no network, no account
 * and no third-party script, which is why it is kept rather than deleted now
 * that a real basemap exists.
 *
 * Longitude is scaled by cos(latitude) so Kenya is not rendered horizontally
 * stretched — the same correction the backend applies to its area estimate.
 */
export function VectorPreview({
  rings, label, className = 'h-48',
}: {
  rings: Ring[]
  label: string
  className?: string
}) {
  const { paths, viewBox } = useMemo(() => {
    const b = ringBounds(rings)
    if (!b) return { paths: [] as string[], viewBox: '0 0 1 1' }

    const midLat = ((b.minLat + b.maxLat) / 2) * (Math.PI / 180)
    const kx = Math.cos(midLat) || 1
    const x = (lon: number) => (lon - b.minLon) * kx
    // SVG y grows downward; latitude grows upward.
    const y = (lat: number) => b.maxLat - lat

    const w = Math.max((b.maxLon - b.minLon) * kx, 1e-6)
    const h = Math.max(b.maxLat - b.minLat, 1e-6)

    const d = rings.map((ring) => {
      const pts = ring
        .filter((p) => typeof p[0] === 'number' && typeof p[1] === 'number')
        .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p[0]).toFixed(5)},${y(p[1]).toFixed(5)}`)
      return pts.length ? `${pts.join(' ')} Z` : ''
    }).filter(Boolean)

    const pad = Math.max(w, h) * 0.04
    return { paths: d, viewBox: `${-pad} ${-pad} ${w + pad * 2} ${h + pad * 2}` }
  }, [rings])

  if (paths.length === 0) return null

  return (
    <div className="border border-border rounded-md bg-muted/20 overflow-hidden">
      <svg
        viewBox={viewBox}
        className={`w-full block ${className}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Boundary of ${label}`}
      >
        {paths.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="var(--color-chocolate)"
            fillOpacity={0.22}
            stroke="var(--color-chocolate)"
            // `vectorEffect: non-scaling-stroke` makes strokeWidth a SCREEN pixel
            // value, not a user-unit one — so this is 1.5px, not 1.5 degrees. The
            // viewBox spans a fraction of a degree, so a user-unit width here
            // would render as no outline at all.
            strokeWidth={1.5}
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
          />
        ))}
      </svg>
    </div>
  )
}
